import classNames from "classnames";
import * as React from "react";

import {
  activePlaceholderTokenAtCursor,
  segmentPlaceholders,
  type PlaceholderSegment,
  type TemplatePath,
  type TriggerTemplateCatalog,
} from "@/shared/domain/triggers";
import { Popover } from "@/shared/foundation/primitives";
import { useCaretAutocomplete, useComboboxIds } from "@/shared/hooks";

import { MirrorHighlightTextarea } from "../MirrorHighlightTextarea";
import tokenStyles from "../topicReferenceTokens.module.css";

import { previewSample } from "./previewSample";
import styles from "./TemplateField.module.css";

/** Suggestions on screen at once; ArrowUp/Down page through the rest. */
const PAGE_SIZE = 6;

/**
 * Chip classes for the highlight overlay. Reuses the skill editor's token
 * palette so a `{{path}}` here and a `[[topic]]` there read as the same kind
 * of thing: a known reference in blue, one the catalog cannot vouch for in
 * amber with a dashed underline.
 */
const SEGMENT_CLASS: Record<PlaceholderSegment["kind"], string | undefined> = {
  plain: undefined,
  bracket: tokenStyles.bracketToken,
  placeholder: tokenStyles.topicToken,
  placeholderUnknown: tokenStyles.topicTokenUnknown,
};

export interface TemplateFieldProps {
  value: string;
  onChange: (next: string) => void;
  /** The paths a placeholder may name, with sample values for the menu. */
  catalog: TriggerTemplateCatalog;
  /** Rendered as the field's own label; pass {@link labelledBy} or {@link ariaLabel} instead when a heading already names it. */
  label?: string;
  /** Id of an element naming the field. */
  labelledBy?: string;
  /** Accessible name when nothing visible names the field. */
  ariaLabel?: string;
  placeholder?: string;
  helperText?: string;
  /** Renders `helperText` as a validation message and outlines the field. */
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
  onBlur?: () => void;
  /**
   * Visible rows. One row (the default) behaves like a text input: it grows
   * with wrapped content and Enter never inserts a newline. More rows make a
   * resizable, multiline editor.
   */
  rows?: number;
  className?: string;
  dataCy?: string;
}

/**
 * A text field for a target template: prose or a value with `{{root.path}}`
 * placeholders resolved from the matched event.
 *
 * Typing `{{` opens a menu of the catalog's paths, filtered as the author
 * types and showing the sample value each resolves to; accepting one writes
 * the closed placeholder and lands the caret after it. Placeholders in the
 * text are highlighted through a mirror overlay (the same mechanics as the
 * skill editor's `[[topic]]` references): a path the catalog knows in one
 * color, one it does not in another, so a typo or a root the subscribed
 * events do not share shows before the validator refuses it at save time.
 */
export const TemplateField = React.forwardRef<
  HTMLTextAreaElement,
  TemplateFieldProps
>(function TemplateField(
  {
    value,
    onChange,
    catalog,
    label,
    labelledBy,
    ariaLabel,
    placeholder,
    helperText,
    error,
    required,
    disabled,
    onBlur,
    rows = 1,
    className,
    dataCy,
  },
  ref,
) {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  // The autocomplete needs the textarea for itself, so the forwarded ref is
  // pointed at the same element rather than given the slot.
  React.useImperativeHandle(ref, () => textareaRef.current!, []);
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = React.useState(false);
  const singleLine = rows <= 1;

  const {
    autocomplete,
    setHighlight,
    clampHighlight,
    refresh,
    anchorEl,
    close,
    syncOverlayScroll,
    handleNavKeyDown,
    accept,
  } = useCaretAutocomplete({
    onChange,
    textareaRef,
    overlayRef,
    paperRef,
    detectToken: activePlaceholderTokenAtCursor,
    anchorTo: "caret",
  });

  const ownLabelId = React.useId();
  const labelId = labelledBy ?? (label !== undefined ? ownLabelId : undefined);
  const { listboxId, optionId } = useComboboxIds();

  // Until the samples arrive nothing can be vouched for, and flagging every
  // placeholder amber for a moment would read as a validation failure.
  const segments = React.useMemo(
    () =>
      segmentPlaceholders(value, (name) =>
        catalog.loaded ? catalog.has(name) : true,
      ),
    [value, catalog],
  );

  const matches: TemplatePath[] = React.useMemo(
    () => (autocomplete === null ? [] : catalog.search(autocomplete.query)),
    [autocomplete, catalog],
  );

  React.useEffect(() => {
    clampHighlight(matches.length);
  }, [clampHighlight, matches.length]);

  const acceptPath = (path: string) => {
    const insertText = `{{${path}}}`;
    // Parking the caret at the end of the insertion (rather than a terminal
    // accept) keeps the engine from appending a space: `{{dataset.name}}.bag`
    // is a common shape here, and a space would break it.
    accept(insertText, insertText.length);
  };

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    onChange(event.target.value);
    refresh(
      event.target.value,
      event.target.selectionEnd ?? event.target.value.length,
    );
  }

  function handleSelect() {
    const textarea = textareaRef.current;
    if (textarea === null) {
      return;
    }
    refresh(textarea.value, textarea.selectionEnd ?? textarea.value.length);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const consumed = handleNavKeyDown(event, matches.length, (index) =>
      acceptPath(matches[index].path),
    );
    if (!consumed && singleLine && event.key === "Enter") {
      event.preventDefault();
    }
  }

  const menuOpen = autocomplete !== null && matches.length > 0;
  const activeOptionId =
    menuOpen && autocomplete !== null
      ? optionId(autocomplete.highlight)
      : undefined;
  const pageStart = autocomplete
    ? Math.floor(autocomplete.highlight / PAGE_SIZE) * PAGE_SIZE
    : 0;
  const pageMatches = matches.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className={classNames(styles.root, className)}>
      {label !== undefined && (
        <label id={ownLabelId} className={styles.label}>
          {`${label}${required ? " *" : ""}`}
        </label>
      )}
      <div
        className={classNames(styles.field, {
          [styles.fieldFocused]: focused,
          [styles.fieldError]: error,
          [styles.fieldDisabled]: disabled,
        })}
      >
        <MirrorHighlightTextarea
          value={value}
          overlay={segments.map((segment, index) => (
            <span key={index} className={SEGMENT_CLASS[segment.kind]}>
              {segment.text}
            </span>
          ))}
          textareaRef={textareaRef}
          overlayRef={overlayRef}
          onChange={handleChange}
          onSelect={handleSelect}
          onScroll={syncOverlayScroll}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          expanded={menuOpen}
          listboxId={listboxId}
          activeOptionId={activeOptionId}
          labelId={labelId}
          ariaLabel={ariaLabel}
          required={required}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          textClassName={classNames(styles.text, {
            [styles.textMultiline]: !singleLine,
          })}
          textareaClassName={classNames(
            styles.textarea,
            singleLine ? styles.textareaSingle : styles.textareaMultiline,
          )}
          dataCy={dataCy}
        />
      </div>
      {helperText !== undefined && (
        <p
          className={classNames(styles.helperText, {
            [styles.helperTextError]: error,
          })}
        >
          {helperText}
        </p>
      )}
      {/* The hook owns when this is up: it already carves the textarea and the
          rows out of its own outside-click handling, which Base UI cannot see,
          so an outside press stays the hook's call. Escape is the exception —
          Base UI takes the key before the textarea's own handler can, so that
          one close is honoured rather than fought. */}
      <Popover
        open={menuOpen && anchorEl !== null}
        onOpenChange={(open, details) => {
          if (!open && details.reason === "escape-key") {
            close();
          }
        }}
      >
        <Popover.Content
          ref={paperRef}
          anchor={anchorEl}
          side="bottom"
          align="start"
          sideOffset={4}
          // The author is still typing; focus must never leave the textarea,
          // which is what `aria-activedescendant` walks the rows from.
          initialFocus={false}
          finalFocus={false}
          className={styles.popup}
        >
          <div id={listboxId} role="listbox" className={styles.suggestions}>
            {pageMatches.map((match, i) => {
              const index = pageStart + i;
              return (
                <div
                  key={match.path}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === autocomplete?.highlight}
                  className={styles.suggestion}
                  onClick={() => acceptPath(match.path)}
                  onMouseEnter={() => setHighlight(index)}
                >
                  <span className={styles.suggestionPath}>{match.path}</span>
                  <span className={styles.suggestionSample}>
                    {previewSample(match.sample)}
                  </span>
                </div>
              );
            })}
          </div>
          <div className={styles.footer}>
            <span className={styles.hint}>
              ↑↓ to navigate · Enter or Tab to insert · Esc to dismiss
            </span>
          </div>
        </Popover.Content>
      </Popover>
    </div>
  );
});
